<template>
    <Card class="quickpay-block-preferences">
        <QuickpaySettingsBlockPreferencesLoader
            v-if="props.isLoadingProviders || (hasConnectedProviders && isLoadingPreferences)"
        />
        <template v-else-if="!hasConnectedProviders">
            <Heading
                :labelTitle="translations.title"
                variant="h2"
                :prefixColoredIcon="{ icon: 'adjustments-horizontal', color: 'brand' }"
            />
            <div class="quickpay-block-preferences__empty-state">
                <CompassEmptyState
                    illustrationPath="/ressources/logged/img/illustrations/payments-empty.svg"
                    :caption="translations.emptyStateDescription"
                />
            </div>
        </template>
        <template v-else>
            <Heading
                :labelTitle="translations.title"
                variant="h2"
                :prefixColoredIcon="{ icon: 'adjustments-horizontal', color: 'brand' }"
            />
            <template v-if="hasOnlineProviders">
                <h3 class="quickpay-block-preferences__section-title typographies-heading-h3">
                    {{ translations.immediatePaymentTitle }}
                </h3>
                <p
                    class="quickpay-block-preferences__section-description typographies-paragraph-medium-regular"
                >
                    {{ translations.immediatePaymentDescription }}
                </p>
                <CompassCheckboxGroup
                    v-model="selectedOnlinePayments"
                    class="quickpay-block-preferences__choices"
                    :disabled="isSaving"
                >
                    <CompassCheckbox
                        v-for="provider in onlineProviders"
                        :key="provider.id"
                        :modelValue="provider.id"
                        :label="provider.label"
                        :caption="provider.caption"
                        :disabled="isSaving"
                    />
                </CompassCheckboxGroup>
            </template>
            <template v-if="hasDirectDebitProviders">
                <h3 class="quickpay-block-preferences__section-title typographies-heading-h3">
                    {{ translations.recurringPaymentTitle }}
                </h3>
                <p
                    class="quickpay-block-preferences__section-description typographies-paragraph-medium-regular"
                >
                    {{ translations.recurringPaymentDescription }}
                </p>
                <CompassRadioGroup
                    v-model="selectedDirectDebit"
                    class="quickpay-block-preferences__choices"
                    direction="vertical"
                    :disabled="isSaving"
                >
                    <CompassRadio
                        v-for="provider in directDebitProviders"
                        :key="provider.id"
                        :modelValue="provider.id"
                        :label="provider.label"
                        :disabled="isSaving"
                    />
                    <CompassRadio
                        modelValue="none"
                        :label="translations.recurringPaymentNone"
                        :disabled="isSaving"
                    />
                </CompassRadioGroup>
            </template>
            <CompassAlert
                class="quickpay-block-preferences__alert"
                :type="CompassEnums.AlertType.NEUTRAL"
                :size="CompassEnums.AlertSize.SMALL"
                :description="translations.alert"
                :showIcon="false"
                :showClose="false"
            />
        </template>
    </Card>
</template>

<script setup lang="ts">
import { ref, computed, watch, inject, onMounted } from 'vue';
import { Translator, PushToast } from 'js/shims-vue';
import Card from 'vcomponents/common/card/Card.vue';
import Heading from 'vcomponents/common/Heading/Heading.vue';
import QuickpaySettingsBlockPreferencesLoader from 'vcomponents/quickpay/settings/QuickpaySettingsBlockPreferencesLoader.vue';
import { fetchPaymentPreferences, savePaymentPreferences } from 'api/quickpay/quickpayService';

interface Props {
    providerStatuses: Record<string, string>;
    isLoadingProviders?: boolean;
}

interface OnlineProvider {
    id: string;
    label: string;
    caption: string;
}

interface DirectDebitProvider {
    id: string;
    label: string;
}

const props = defineProps<Props>();

const $t = inject('$t') as Translator;
const $pushToast = inject('$pushToast') as PushToast;

const ONLINE_PAYMENT_PROVIDER_IDS = ['stancer', 'stripe', 'paypal', 'adyen', 'atos'];
const DIRECT_DEBIT_PROVIDER_IDS = ['gocardless', 'stripe'];

const PROVIDER_CONFIG: Record<
    string,
    {
        onlineLabelKey?: string;
        onlineCaptionKey?: string;
        directDebitLabelKey?: string;
    }
> = {
    stancer: {
        onlineLabelKey: 'quickpay.settings.preferences.providers.stancer.label',
        onlineCaptionKey: 'quickpay.settings.preferences.providers.stancer.caption',
    },
    stripe: {
        onlineLabelKey: 'quickpay.settings.preferences.providers.stripe.label',
        onlineCaptionKey: 'quickpay.settings.preferences.providers.stripe.caption',
        directDebitLabelKey: 'quickpay.settings.preferences.providers.stripe.directDebit.label',
    },
    paypal: {
        onlineLabelKey: 'quickpay.settings.preferences.providers.paypal.label',
        onlineCaptionKey: 'quickpay.settings.preferences.providers.paypal.caption',
    },
    adyen: {
        onlineLabelKey: 'quickpay.settings.preferences.providers.adyen.label',
        onlineCaptionKey: 'quickpay.settings.preferences.providers.adyen.caption',
    },
    atos: {
        onlineLabelKey: 'quickpay.settings.preferences.providers.atos.label',
        onlineCaptionKey: 'quickpay.settings.preferences.providers.atos.caption',
    },
    gocardless: {
        directDebitLabelKey: 'quickpay.settings.preferences.providers.gocardless.directDebit.label',
    },
};

const translations = {
    title: $t.__('quickpay.settings.preferences.title'),
    emptyStateDescription: $t.__('quickpay.settings.preferences.emptyState.description'),
    immediatePaymentTitle: $t.__('quickpay.settings.preferences.immediatePayment.title'),
    immediatePaymentDescription: $t.__(
        'quickpay.settings.preferences.immediatePayment.description',
    ),
    recurringPaymentTitle: $t.__('quickpay.settings.preferences.recurringPayment.title'),
    recurringPaymentDescription: $t.__(
        'quickpay.settings.preferences.recurringPayment.description',
    ),
    recurringPaymentNone: $t.__('quickpay.settings.preferences.recurringPayment.none'),
    alert: $t.__('quickpay.settings.preferences.alert'),
};

const isLoadingPreferences = ref(true);
const isInitialized = ref(false);
const isSaving = ref(false);
const selectedOnlinePayments = ref<string[]>([]);
const selectedDirectDebit = ref<string>('none');

const connectedProviderIds = computed((): string[] => {
    return Object.entries(props.providerStatuses)
        .filter(([, status]) => status === 'connected')
        .map(([id]) => id);
});

// Connected providers that support immediate (online) payment, with translated labels
const onlineProviders = computed((): OnlineProvider[] => {
    return ONLINE_PAYMENT_PROVIDER_IDS.flatMap((id) => {
        const config = PROVIDER_CONFIG[id];
        if (
            !connectedProviderIds.value.includes(id) ||
            !config?.onlineLabelKey ||
            !config?.onlineCaptionKey
        ) {
            return [];
        }
        return {
            id,
            label: $t.__(config.onlineLabelKey),
            caption: $t.__(config.onlineCaptionKey),
        };
    });
});

// Connected providers that support recurring (direct debit) payment, with translated labels
const directDebitProviders = computed((): DirectDebitProvider[] => {
    return DIRECT_DEBIT_PROVIDER_IDS.flatMap((id) => {
        const config = PROVIDER_CONFIG[id];
        if (!connectedProviderIds.value.includes(id) || !config?.directDebitLabelKey) {
            return [];
        }
        return {
            id,
            label: $t.__(config.directDebitLabelKey),
        };
    });
});

const hasOnlineProviders = computed((): boolean => onlineProviders.value.length > 0);

const hasDirectDebitProviders = computed((): boolean => directDebitProviders.value.length > 0);

const hasConnectedProviders = computed(
    (): boolean => hasOnlineProviders.value || hasDirectDebitProviders.value,
);

const loadPreferences = async (): Promise<void> => {
    try {
        const response = await fetchPaymentPreferences();
        const data = response.data as { onlinePayments: string[]; directDebit: string | false };
        selectedOnlinePayments.value = data.onlinePayments ?? [];
        selectedDirectDebit.value = data.directDebit || 'none';
    } catch {
        $pushToast({
            type: 'error',
            icon: true,
            text: $t.__('common.labels.toast.error'),
        });
    } finally {
        isLoadingPreferences.value = false;
        isInitialized.value = true;
    }
};

const save = async (): Promise<void> => {
    isSaving.value = true;
    try {
        await savePaymentPreferences({
            onlinePayments: selectedOnlinePayments.value,
            directDebit: selectedDirectDebit.value === 'none' ? false : selectedDirectDebit.value,
        });
    } catch {
        $pushToast({
            type: 'error',
            icon: true,
            text: $t.__('common.labels.toast.error'),
        });
    } finally {
        isSaving.value = false;
    }
};

watch(
    selectedOnlinePayments,
    () => {
        if (isInitialized.value) {
            save();
        }
    },
    { deep: true },
);
watch(selectedDirectDebit, () => {
    if (isInitialized.value) {
        save();
    }
});

watch(hasConnectedProviders, (nowHasProviders) => {
    if (nowHasProviders && !isInitialized.value) {
        isLoadingPreferences.value = true;
        loadPreferences();
    }
});

onMounted(() => {
    if (hasConnectedProviders.value) {
        loadPreferences();
    } else {
        isLoadingPreferences.value = false;
    }
});
</script>

<style lang="scss" scoped>
.quickpay-block-preferences {
    margin-top: var(--spacing-4);

    &__empty-state {
        margin-top: var(--spacing-4);
    }

    &__section-title {
        margin-top: var(--spacing-3);
        margin-bottom: var(--spacing-1);
    }

    &__section-description {
        color: var(--colors-common-text-subtle-default);
        margin-bottom: var(--spacing-2);
    }

    &__alert {
        margin-top: var(--spacing-4);
        margin-bottom: 0;
    }
}
</style>
